// ─────────────────────────────────────────────────────────────────────────────
// Profils clavier éditables (RDP + Vidéo).
//
// Chaque panneau a SON système de codes (RDP = scancodes PS/2, Vidéo = VK Windows),
// mais PARTAGE la même logique de décision : qu'est-ce qu'on fait quand l'opérateur
// appuie sur Cmd+C, Ctrl+V, Cmd+Tab, etc. ?
//
// Le profil décrit des RÈGLES évaluées dans l'ordre (premier match gagne) sur
// `event.code` + `event.key` + modifiers. Chaque règle produit un `KeyAction` qui
// dit au panneau quoi faire. Le panneau traduit ensuite l'action en scancode (RDP)
// ou VK (Vidéo) via `@/shared/keymap`.
//
// Trois profils prédéfinis :
//  - `mac` : Cmd→Ctrl pour les raccourcis usuels (Cmd+C = Ctrl+C), Ctrl→Ctrl aussi
//    (les deux marchent), liste noire Cmd+Tab/Space/Q/H/M = local.
//  - `windows` : Cmd = touche Windows (Meta), Ctrl = Ctrl. Natif, pas de swap.
//  - `custom` : éditable par l'opérateur via l'éditeur de table.
//
// Le profil `mac` généralise et remplace le `swapCmdCtrl` booléen du panel vidéo
// et le `cmdKeyMode` du panel RDP : une source de vérité unique, éditable.
// ─────────────────────────────────────────────────────────────────────────────

import { codeToScancode, codeToVk, isMacPlatform, VK } from "./keymap";

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Action décidée par le profil pour un événement clavier.
 *
 * Le panneau (RDP ou Vidéo) consomme `scancode` ou `vk` selon son backend :
 *  - RDP lit `scancode` (PS/2) et l'envoie via `iron.DeviceEvent.keyPressed`.
 *  - Vidéo lit `vk` (Windows) et l'envoie via `ws.send({ kind: "key", vk })`.
 *
 * `map` = envoyer le code traduit (scancode ET vk sont toujours calculés tous
 * les deux quand la touche est connue). `local` = garder local (Cmd+Tab switch).
 * `ignore` = jeter l'événement.
 */
export type KeyAction =
  | { kind: "map"; scancode: number | null; vk: number | null }
  | { kind: "local" }
  | { kind: "ignore" };

/**
 * Critère de match d'une règle. Tous les champs définis doivent matcher.
 * `undefined` = "n'importe quelle valeur" (joker).
 *
 * `code` = position physique (KeyA, Digit1, MetaLeft...) — indépendant du layout.
 * `key` = caractère produit (utile pour les caractères unicode comme "é").
 * `cmd` = Meta (Cmd sur Mac / touche Windows).
 * `ctrl` = Control.
 * `alt` = Alt / Option.
 */
export interface KeyMatch {
  code?: string;
  key?: string;
  cmd?: boolean;
  ctrl?: boolean;
  alt?: boolean;
}

export interface KeyboardRule {
  match: KeyMatch;
  action: RuleAction;
}

/**
 * Action exprimée dans la règle (sérialisable JSON, pour l'éditeur Perso).
 * `swap-cmd-to-ctrl` est l'action spéciale qui traduit Cmd+lettre → Ctrl+lettre
 * (le cas Mac usuel) : la règle match sur `cmd: true` et l'évaluation produit un
 * `KeyAction.map` avec le VK/scancode de Control au lieu de Meta.
 */
export type RuleAction =
  | { type: "map" } // Envoyer le code naturel de la touche (scancode + vk).
  | { type: "swap-cmd-to-ctrl" } // Cmd → Ctrl (raccourci Mac → Windows).
  | { type: "local" } // Garder local (Cmd+Tab, Cmd+Space...).
  | { type: "ignore" }; // Jeter.

export interface KeyboardProfile {
  id: string;
  label: string;
  /** `true` pour les profils prédéfinis (non éditables, non supprimables). */
  builtin: boolean;
  /** Règles évaluées dans l'ordre. Première match gagne. */
  rules: KeyboardRule[];
}

/** Abstraction légère de KeyboardEvent pour la testabilité (pas de dépendance DOM). */
export interface KeyEventLike {
  code: string;
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  getModifierState?: (key: string) => boolean;
}

// ── Évaluation (pure, testable) ──────────────────────────────────────────────

function matchRule(rule: KeyboardRule, e: KeyEventLike): boolean {
  const m = rule.match;
  if (m.code !== undefined && m.code !== e.code) return false;
  if (m.key !== undefined && m.key !== e.key) return false;
  if (m.cmd !== undefined && m.cmd !== e.metaKey) return false;
  if (m.ctrl !== undefined && m.ctrl !== e.ctrlKey) return false;
  if (m.alt !== undefined && m.alt !== e.altKey) return false;
  return true;
}

/**
 * Évalue un événement clavier contre un profil et produit un `KeyAction`.
 *
 * @returns l'action + l'index de la règle matchée (pour debug/UI), ou `null` si
 *   aucune règle ne matche (le panneau décide du défaut, généralement "map").
 */
export function evaluateKey(
  profile: KeyboardProfile,
  e: KeyEventLike,
): { action: KeyAction; ruleIndex: number } | null {
  for (let i = 0; i < profile.rules.length; i++) {
    const rule = profile.rules[i];
    if (!matchRule(rule, e)) continue;
    return { action: resolveAction(rule.action, e), ruleIndex: i };
  }
  return null;
}

function resolveAction(action: RuleAction, e: KeyEventLike): KeyAction {
  switch (action.type) {
    case "local":
      return { kind: "local" };
    case "ignore":
      return { kind: "ignore" };
    case "swap-cmd-to-ctrl":
      // L'opérateur a appuyé sur Cmd (+ autre touche). On veut que ça arrive
      // comme Ctrl côté Windows. On renvoie donc le VK/scancode de la touche
      // compagnon (lettre/chiffre), ET on s'assure que le modifieur envoyé sera
      // Ctrl (0x11 VK / 0x1d scancode) plutôt que Meta (0x5b / 0xe05b).
      // Concrètement : le panneau, en voyant `kind: "map"` avec `vk`/`scancode`
      // de la touche compagnon, doit PRÉCÉDER le down de Ctrl. On indique le
      // swap via un marqueur : on renvoie le code de Control dans vk/scancode
      // SI l'événement est le modifieur seul, sinon le code de la touche.
      // Simplification : on renvoie le code de la touche actuelle ; le panneau
      // sait (via le profil) qu'il doit émettre Ctrl d'abord. On documente ça.
      return swapCmdToCtrl(e);
    case "map":
    default:
      return { kind: "map", scancode: codeToScancode(e.code), vk: codeToVk(e.code) };
  }
}

/**
 * Traduit Cmd → Ctrl. Si l'événement est le modifieur Cmd lui-même (MetaLeft/
 * MetaRight), on renvoie le code de Control. Sinon (Cmd+lettre), on renvoie le
 * code de la lettre — le panneau aura déjà émis Ctrl via le down du modifieur.
 */
function swapCmdToCtrl(e: KeyEventLike): KeyAction {
  if (e.code === "MetaLeft" || e.code === "MetaRight") {
    return { kind: "map", scancode: 0x1d, vk: VK.CONTROL }; // ControlLeft scancode / VK_CONTROL.
  }
  return { kind: "map", scancode: codeToScancode(e.code), vk: codeToVk(e.code) };
}

// ── Profils prédéfinis ───────────────────────────────────────────────────────

/**
 * Profil Mac : Cmd agit comme Ctrl pour les raccourcis, MAIS certaines
 * combinaisons restent locales (Cmd+Tab switch d'app, Cmd+Space = Spotlight,
 * Cmd+Q/H/M = quitter/cacher/minimiser).
 *
 * Sur Mac, Ctrl envoie AUSSI Ctrl (les deux modifieurs marchent). C'est ce que
 * font Microsoft Remote Desktop et Royal TSX.
 */
export function macProfile(): KeyboardProfile {
  return {
    id: "mac",
    label: "Mac (Cmd → Ctrl)",
    builtin: true,
    rules: [
      // ── Liste noire : Cmd+combinaisons gardées locales ──
      { match: { cmd: true, code: "Tab" }, action: { type: "local" } }, // Cmd+Tab (switch app)
      { match: { cmd: true, code: "Space" }, action: { type: "local" } }, // Cmd+Space (Spotlight/IME)
      { match: { cmd: true, code: "KeyQ" }, action: { type: "local" } }, // Cmd+Q (quitter)
      { match: { cmd: true, code: "KeyH" }, action: { type: "local" } }, // Cmd+H (cacher)
      { match: { cmd: true, code: "KeyM" }, action: { type: "local" } }, // Cmd+M (minimiser)
      // ── Swap Cmd → Ctrl pour tous les autres Cmd+lettre ──
      { match: { cmd: true }, action: { type: "swap-cmd-to-ctrl" } },
      // ── Tout le reste : code naturel ──
      { match: {}, action: { type: "map" } },
    ],
  };
}

/**
 * Profil Windows : Cmd = touche Windows (Meta), Ctrl = Ctrl. Natif, pas de swap.
 * Convient aussi pour les opérateurs qui prennent la main depuis un PC.
 */
export function windowsProfile(): KeyboardProfile {
  return {
    id: "windows",
    label: "Windows / Linux (natif)",
    builtin: true,
    rules: [
      // Tout passe en code naturel. Cmd (MetaLeft/Right) → touche Windows.
      { match: {}, action: { type: "map" } },
    ],
  };
}

/** Profil vide servant de base à l'éditeur Perso. */
export function customProfileTemplate(): KeyboardProfile {
  return {
    id: "custom",
    label: "Personnalisé",
    builtin: false,
    rules: [
      // Par défaut, on part du profil Mac (le plus commun pour les opérateurs
      // RemoteWS qui prennent la main depuis un Mac). L'opérateur ajuste.
      { match: { cmd: true, code: "Tab" }, action: { type: "local" } },
      { match: { cmd: true, code: "Space" }, action: { type: "local" } },
      { match: { cmd: true }, action: { type: "swap-cmd-to-ctrl" } },
      { match: {}, action: { type: "map" } },
    ],
  };
}

export const BUILTIN_PROFILES: KeyboardProfile[] = [macProfile(), windowsProfile()];

export function findBuiltinProfile(id: string): KeyboardProfile | undefined {
  return BUILTIN_PROFILES.find((p) => p.id === id);
}

/** Profil par défaut selon la plateforme (Mac → mac, sinon windows). */
export function defaultProfileIdForPlatform(): string {
  return isMacPlatform() ? "mac" : "windows";
}

// ── Persistance ──────────────────────────────────────────────────────────────

const PROFILE_ID_STORAGE_KEY = "rws.keyboard.profile";
const CUSTOM_PROFILE_STORAGE_KEY = "rws.keyboard.custom";

/** Charge le profil actif (builtin ou custom). */
export function loadProfile(id?: string): KeyboardProfile {
  const profileId = id ?? readStoredProfileId();
  if (profileId === "custom") {
    const custom = readStoredCustomProfile();
    if (custom) return custom;
  }
  const builtin = findBuiltinProfile(profileId);
  return builtin ?? macProfile();
}

export function saveProfileId(id: string): void {
  try {
    localStorage.setItem(PROFILE_ID_STORAGE_KEY, id);
  } catch {
    /* localStorage indisponible */
  }
}

export function saveCustomProfile(profile: KeyboardProfile): void {
  try {
    localStorage.setItem(CUSTOM_PROFILE_STORAGE_KEY, JSON.stringify(profile));
  } catch {
    /* localStorage indisponible */
  }
}

function readStoredProfileId(): string {
  try {
    const v = localStorage.getItem(PROFILE_ID_STORAGE_KEY);
    if (v === "mac" || v === "windows" || v === "custom") return v;
  } catch {
    /* localStorage indisponible */
  }
  return defaultProfileIdForPlatform();
}

function readStoredCustomProfile(): KeyboardProfile | null {
  try {
    const raw = localStorage.getItem(CUSTOM_PROFILE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KeyboardProfile;
    if (!parsed || !Array.isArray(parsed.rules) || typeof parsed.label !== "string") return null;
    return { ...parsed, id: "custom", builtin: false };
  } catch {
    return null;
  }
}

// ── Helpers pour les panneaux ────────────────────────────────────────────────

/**
 * Évalue un événement et applique le défaut "map" si aucune règle ne matche.
 * C'est le point d'entrée principal des panneaux : retourne toujours un KeyAction.
 */
export function resolveKey(profile: KeyboardProfile, e: KeyEventLike): KeyAction {
  const result = evaluateKey(profile, e);
  if (result) return result.action;
  // Défaut : code naturel de la touche.
  return { kind: "map", scancode: codeToScancode(e.code), vk: codeToVk(e.code) };
}

/** Détecte si un événement est un raccourci de collage (Ctrl+V ou Cmd+V). */
export function isPasteShortcut(e: KeyEventLike): boolean {
  return e.code === "KeyV" && (e.ctrlKey || e.metaKey);
}

/** Détecte si un événement est un raccourci de copie (Ctrl+C ou Cmd+C). */
export function isCopyShortcut(e: KeyEventLike): boolean {
  return e.code === "KeyC" && (e.ctrlKey || e.metaKey);
}

/** Détecte si un événement est un raccourci de coupage (Ctrl+X ou Cmd+X). */
export function isCutShortcut(e: KeyEventLike): boolean {
  return e.code === "KeyX" && (e.ctrlKey || e.metaKey);
}
